import React, { useState, useEffect, useRef } from 'react';
import { publicAPI } from '../services/publicAPI';
import { DashboardUser } from '../types';
import './PublicDashboard.css';
import logoImage from './HTML/logo.png';
import lineImage from './HTML/line.png';

const API_BASE = 'https://vlad.voxlink.ru/api';

type EmployeeStatus = {
  name: string;
  phone: string;
  redis_status: string;
  emoji: string;
  available: number;
  city: string;
  role: string;
};

const PublicDashboard: React.FC = () => {
  const [queue, setQueue] = useState<DashboardUser[]>([]);
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [hue, setHue] = useState(200);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Record<string, EmployeeStatus>>({});

  const idxRef = useRef(idx);
  idxRef.current = idx;
  const queueRef = useRef(queue);
  queueRef.current = queue;

  // Загрузка пользователей
  useEffect(() => {
    publicAPI.getDashboardData()
      .then(data => {
        const shuffled = [...data.users].sort(() => Math.random() - 0.5);
        setQueue(shuffled);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Загрузка статусов — сразу и раз в минуту
  useEffect(() => {
    const load = () => publicAPI.getEmployeeStatuses()
      .then(setStatuses)
      .catch(console.error);
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  // Смена сотрудника раз в минуту
  useEffect(() => {
    if (queue.length === 0) return;
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        const next = (idxRef.current + 1) % queueRef.current.length;
        setIdx(next);
        // Меняем цвет фона на новый при смене сотрудника
        setHue(h => (h + 137) % 360);
        setFading(false);
      }, 400);
    }, 60_000);
    return () => clearInterval(t);
  }, [queue.length]);

  if (loading) return (
    <div className="pd-loading">Загрузка данных...</div>
  );

  if (queue.length === 0) return (
    <div className="pd-loading">Нет данных для отображения</div>
  );

  const user = queue[idx];
  if (!user) return null;

  const nameParts = user.full_name.split(' ');
  const lastName = (nameParts[0] || '').toUpperCase();
  const firstPatronymic = nameParts.slice(1).join(' ');

  const bgFrom = `hsl(${hue},60%,22%)`;
  const bgTo = '#211e63';

  // Список сотрудников со статусами для стрипа
  const statusList = Object.values(statuses);

  // Статус текущего отображаемого сотрудника (поиск по номеру телефона)
  const currentUserEmoji = user.phone_number
    ? statuses[user.phone_number]?.emoji ?? null
    : null;

  return (
    <div className="pd-wrap">
      <main
        className={`canvas${fading ? ' fading' : ''}`}
        style={{ background: `linear-gradient(to top right, ${bgFrom} 0%, ${bgTo} 100%)` }}
      >
        <img src={lineImage} alt="" className="lines-image" />

        {/* Фото */}
        <div className="photo-card">
          {user.avatar_path ? (
            <img src={`${API_BASE}/uploads/${user.avatar_path}`} alt={user.full_name} />
          ) : (
            <div className="photo-placeholder">
              {user.full_name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Метка внутренний */}
        <div className="internal">
          <svg viewBox="0 0 24 24" width="28" height="28">
            <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.28-.28.67-.36 1.02-.25 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C10.01 21 3 13.99 3 4c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
          </svg>
          Внутренний
        </div>

        {/* Номер */}
        <div className="big-number">{user.phone_number}</div>

        {/* Имя */}
        <div className="name-block">
          <div className="name">{lastName}</div>
          <div className="fullname">{firstPatronymic}</div>
          {currentUserEmoji && (
            <div className="user-status-emoji">{currentUserEmoji}</div>
          )}
        </div>

        {/* Должность */}
        <div className="position">{user.position || 'Сотрудник'}</div>

        {/* Компания */}
        <div className="company-box">
          <div className="brand-line">
            <img src={logoImage} alt="voxlink" style={{ height: '40px' }} />
          </div>
          <div className="company-name">Вокс Линк</div>
          <div className="city">({user.city || 'Москва'})</div>
        </div>

        {/* Нижний блок */}
        <div className="bottom-info">
          <div>
            <div className="info-label">В компании:</div>
            <div className="years-text">
              <span className="small">С</span> {user.company_start_year || new Date().getFullYear() - 2} <span className="small">ГОДА</span>
            </div>
          </div>
          <div>
            <div className="info-label">Компетенции:</div>
            <div className="skills-text">
              {user.competencies && user.competencies.length > 0
                ? String(user.competencies).split(',').slice(0, 100).join(', ')
                : 'Компетенции не указаны'}
            </div>
          </div>
        </div>

        {/* Стрип статусов — всегда виден */}
        <div className="status-strip">
          {statusList.length === 0 ? (
            <div className="sc-loading">Загрузка статусов...</div>
          ) : (
            statusList.map((emp) => {
              const parts = emp.name.split(' ');
              const firstName = parts[0] || '';
              const lastName = parts[1] || '';
              const shortName = lastName
                ? `${firstName.charAt(0)}. ${lastName}`
                : firstName;
              return (
                <div className="status-card" key={emp.phone} title={emp.redis_status}>
                  <span className="sc-emoji">{emp.emoji}</span>
                  <span className="sc-phone">{emp.phone}</span>
                  <span className="sc-name">{shortName}</span>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
};

export default PublicDashboard;
